import { Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import {
  AccountSortField,
  AccountsService,
  IAccount,
  SortDirection
} from '../../services/accounts.service';
import {NgxFilesizeModule} from 'ngx-filesize';

type UserTypeOption = {
  label: string;
  value: string;
};

@Component({
  selector: 'app-accounts-table',
  imports: [DatePipe, NgxFilesizeModule],
  templateUrl: './accounts-table.component.html',
  styleUrl: './accounts-table.component.css'
})
export class AccountsTableComponent {
  private readonly accountsService = inject(AccountsService);
  private readonly authenticator = inject(AuthenticatorService);

  protected readonly accounts = signal<IAccount[]>([]);
  protected readonly totalAccounts = signal<number>(0);
  protected readonly recordsFound = signal<number>(0);
  protected readonly selectedUserType = signal<string>('');
  protected readonly sortField = signal<AccountSortField>('createdOn');
  protected readonly sortDirection = signal<SortDirection>('asc');
  protected readonly isDeleteOpen = signal<boolean>(false);
  protected readonly pendingDelete = signal<IAccount | null>(null);
  protected readonly page = signal<number>(1);
  protected readonly pageSize = signal<number>(10);
  protected readonly totalPages = signal<number>(1);
  protected readonly isDeleting = signal<boolean>(false);

  protected readonly userTypeOptions: UserTypeOption[] = [
    { label: 'All', value: '' },
    { label: 'User', value: 'user' },
    { label: 'Releaser', value: 'releaser' }
  ];

  constructor() {
    void this.loadAccounts();
  }

  protected async loadAccounts(): Promise<void> {
    const response = await firstValueFrom(
      this.accountsService.listAccounts({
        sortField: this.sortField(),
        sortDirection: this.sortDirection(),
        userType: this.selectedUserType() || undefined,
        page: this.page(),
        pageSize: this.pageSize()
      })
    );

    this.accounts.set(response.items);
    this.totalAccounts.set(response.totalCount);
    this.recordsFound.set(response.recordsFound);
    const totalForPages = response.totalCount || response.recordsFound;
    this.totalPages.set(Math.max(1, Math.ceil(totalForPages / response.pageSize)));
  }

  protected onSort(field: AccountSortField): void {
    if (this.sortField() === field) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDirection.set('asc');
    }

    this.page.set(1);
    void this.loadAccounts();
  }

  protected sortIndicator(field: AccountSortField): string {
    if (this.sortField() !== field) {
      return 'sort';
    }
    return this.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  protected ariaSort(field: AccountSortField): 'ascending' | 'descending' | 'none' {
    if (this.sortField() !== field) {
      return 'none';
    }
    return this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  protected onUserTypeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedUserType.set(target.value);
    this.page.set(1);
    void this.loadAccounts();
  }

  protected onDelete(account: IAccount): void {
    this.pendingDelete.set(account);
    this.isDeleteOpen.set(true);
  }

  protected confirmDelete(): void {
    const account = this.pendingDelete();
    if (!account) {
      return;
    }
    void this.performDelete(account._id);
  }

  protected closeDeleteModal(): void {
    this.isDeleteOpen.set(false);
    this.pendingDelete.set(null);
  }

  protected goToPage(page: number): void {
    const nextPage = Math.min(Math.max(1, page), this.totalPages());
    if (nextPage === this.page()) {
      return;
    }
    this.page.set(nextPage);
    void this.loadAccounts();
  }

  protected userTypeLabel(account: IAccount): string {
    const { person, releaser } = account.usertype ?? {};

    if (person && releaser) return 'Both';
    if (person) return 'User';
    if (releaser) return 'Releaser';
    return 'Unknown';
  }

  protected socialProvider(account: IAccount): string {
    return account.social?.providerName ?? 'N/A';
  }

  protected tierLabel(account: IAccount): string {
    return account.subscription?.tier ? String(account.subscription.tier) : 'N/A';
  }

  private async performDelete(accountId: string): Promise<void> {
    this.isDeleting.set(true);
    try {
      await firstValueFrom(this.accountsService.deleteAccount(accountId));
    } finally {
      this.isDeleting.set(false);
      this.closeDeleteModal();
      if (this.page() > 1 && this.accounts().length === 1) {
        this.page.set(this.page() - 1);
      }
      await this.loadAccounts();
    }
  }
}
